import { WalkthroughButton } from "@/components/WalkthroughButton";
import { ArrowLeft, Download, Smartphone, WandSparkles } from "lucide-react";
import React, { useContext, useState, useEffect } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { GroupedNav } from "@/components/GroupedNav";
import { 
  LayoutDashboard, 
  Users, 
  Zap, 
  Calendar, 
  FileText, 
  Settings,
  LogOut,
  Bell,
  Search,
  ChevronDown,
  UsersRound,
  Contact,
  ClipboardList,
  CheckSquare,
  DollarSign,
  BarChart3,
  Workflow,
  MessageSquareMore,
  MessageSquare,
  Menu,
  X,
  UserCheck,
  Factory,
  Package,
  Table2,
  ShoppingCart,
  Clock,
  Shield,
  Star,
  FileSignature,
  Cpu
} from "lucide-react";
import { AuthContext, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import NotificationsDropdown from "@/components/NotificationsDropdown";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";

// Navigation items - filtered by role in the component
const allNavItems = [
  { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard", roles: ["owner", "project_manager", "office_admin", "employee", "subcontractor"] },
  { to: "/leads", icon: Contact, label: "Leads", roles: ["owner"] },
  { to: "/customers", icon: UserCheck, label: "Customers", roles: ["owner", "project_manager", "office_admin"] },
  { to: "/orders", icon: ShoppingCart, label: "Orders", roles: ["owner", "project_manager", "office_admin"] },
  { to: "/estimates", icon: DollarSign, label: "Estimates", roles: ["owner", "project_manager", "office_admin"] },
  { to: "/calendar", icon: Calendar, label: "Schedule", roles: ["owner", "project_manager", "office_admin", "employee", "subcontractor"] },
  { to: "/timecard", icon: Clock, label: "Timecard", roles: ["owner", "project_manager", "office_admin", "employee", "subcontractor"] },
  { to: "/chat", icon: MessageSquare, label: "Chat", roles: ["owner", "project_manager", "office_admin", "employee", "subcontractor"] },
  { to: "/tasks", icon: CheckSquare, label: "Tasks", roles: ["owner", "project_manager", "office_admin", "employee", "subcontractor"] },
  { to: "/customer-requests", icon: MessageSquareMore, label: "Customer Requests", roles: ["owner", "project_manager"] },
  { to: "/analytics", icon: BarChart3, label: "Analytics", roles: ["owner"] },
  { to: "/team", icon: UsersRound, label: "Team", roles: ["owner", "office_admin"] },
  { to: "/suppliers", icon: Factory, label: "Suppliers", roles: ["owner", "office_admin"] },
  { to: "/procurement", icon: Package, label: "Material Procurement", roles: ["owner", "project_manager", "office_admin"] },
  { to: "/production-sheets", icon: Table2, label: "Production Sheets", roles: ["owner", "project_manager", "office_admin"] },
  { to: "/cnc-router-station", icon: Cpu, label: "CNC Router Station", testId: "nav-link-cnc-router-station", roles: ["owner", "project_manager", "office_admin", "employee"] },
  { to: "/automation-rules", icon: Workflow, label: "Automations", roles: ["owner"] },
  { to: "/forms", icon: FileText, label: "Forms", roles: ["owner", "project_manager", "office_admin"] },
  { to: "/reviews", icon: Star, label: "Reviews", roles: ["owner", "project_manager", "office_admin"] },
  { to: "/contracts", icon: FileSignature, label: "Contracts & Invoices", roles: ["owner", "project_manager", "office_admin"] },
  { to: "/settings", icon: Settings, label: "Settings", roles: ["owner", "project_manager", "office_admin", "employee", "subcontractor"] },
  { to: "/approvals", icon: Shield, label: "Approvals", roles: ["owner"] },
];

export const Layout = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [pendingApprovals, setPendingApprovals] = useState(0);
  useEffect(() => {
    if (user?.role !== "owner") return;
    axios.get(`${API}/approvals`).then(r => setPendingApprovals((r.data || []).filter(a => a.status === "pending").length)).catch(() => {});
  }, [user?.role]);
  
  // Filter nav items based on user role
  const navItems = allNavItems.filter(item => 
    item.roles.includes(user?.role || "owner")
  );

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
      toast.success("Logged out successfully");
      navigate("/login", { replace: true });
    } catch (error) {
      navigate("/login", { replace: true });
    }
  };

  const getInitials = (name) => {
    if (!name) return "U";
    return name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
  };

  const handleNavClick = () => {
    setSidebarOpen(false);
  };

  const handleGlobalBack = () => {
    if (location.key && location.key !== "default") {
      navigate(-1);
      return;
    }
    navigate("/dashboard");
  };
  const isFullBleedWorkspace = location.pathname === "/cnc-router-station";
  const canAccessCnc = ["owner", "project_manager", "office_admin", "employee"].includes(user?.role);

  const SidebarContent = () => (
    <>
      {/* Logo */}
      <div className="h-16 flex items-center px-6 border-b border-slate-200">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center">
            <Zap className="w-5 h-5 text-white" />
          </div>
          <span className="font-bold text-xl text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Sage CRM
          </span>
        </div>
      </div>

      {/* Navigation */}
      <GroupedNav navItems={navItems} onNavigate={handleNavClick} badges={{ "/approvals": pendingApprovals }} />

      {/* Install App Button */}
      <InstallAppButton />

      {/* User section */}
      <div className="p-4 border-t border-slate-200">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button 
              className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-slate-100 transition-colors"
              data-testid="user-menu-trigger"
            >
              <Avatar className="h-9 w-9">
                <AvatarImage src={user?.picture} alt={user?.name} />
                <AvatarFallback className="bg-indigo-100 text-indigo-700">
                  {getInitials(user?.name)}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 text-left min-w-0">
                <p className="text-sm font-medium text-slate-900 truncate">{user?.name}</p>
                <p className="text-xs text-slate-500 capitalize">{user?.role?.replace("_", " ") || "Owner"}</p>
              </div>
              <ChevronDown className="w-4 h-4 text-slate-400" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuItem onClick={() => { navigate("/settings"); handleNavClick(); }}>
              <Settings className="w-4 h-4 mr-2" />
              Settings
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleLogout} className="text-red-600">
              <LogOut className="w-4 h-4 mr-2" />
              Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </>
  );

  return (
    <div className="h-[100dvh] flex flex-col bg-slate-50 overflow-hidden" style={{ height: "100dvh" }}>
      {/* Mobile/Collapsible Sidebar using Sheet */}
      <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
        <SheetContent side="left" className="w-64 p-0 flex flex-col">
          <SheetHeader className="sr-only">
            <SheetTitle>CRM navigation</SheetTitle>
            <SheetDescription>Open a Sage CRM workspace.</SheetDescription>
          </SheetHeader>
          <SidebarContent />
        </SheetContent>
      </Sheet>

      {/* Main content - full width */}
      <div className="flex-1 flex flex-col min-h-0">
        {/* Top header */}
        <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-2 sm:px-4 shrink-0 z-10">
          <div className="flex min-w-0 items-center gap-2 sm:gap-4">
            {/* Hamburger Menu Button */}
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => setSidebarOpen(true)}
              data-testid="menu-toggle"
            >
              <Menu className="w-5 h-5" />
            </Button>
            
            {/* Logo in header */}
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 bg-indigo-600 rounded-lg flex items-center justify-center">
                <Zap className="w-4 h-4 text-white" />
              </div>
              <span className="font-bold text-lg text-slate-900 hidden sm:block" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Sage CRM
              </span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleGlobalBack}
              className="h-8 gap-1 px-2 text-slate-600 hover:text-slate-950"
              aria-label="Go back"
              data-testid="global-back-button"
            >
              <ArrowLeft className="h-4 w-4" />
              <span>Back</span>
            </Button>
            {canAccessCnc && (
              <TooltipProvider delayDuration={250}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => navigate("/cnc-router-station")}
                      className={`h-9 w-9 shrink-0 border transition-[background-color,border-color,color,transform] hover:-translate-y-0.5 ${location.pathname === "/cnc-router-station" ? "border-cyan-600 bg-cyan-600 text-white hover:bg-cyan-700 hover:text-white" : "border-cyan-200 bg-cyan-50 text-cyan-700 hover:border-cyan-300 hover:bg-cyan-100 hover:text-cyan-800"}`}
                      aria-label="CNC Router Station"
                      data-testid="topbar-cnc-shortcut"
                    >
                      <Cpu className="h-[18px] w-[18px]" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" data-testid="topbar-cnc-shortcut-tooltip">CNC Router Station</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            {user?.role === "owner" && (
              <TooltipProvider delayDuration={250}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button type="button" variant="ghost" size="icon" onClick={() => navigate("/quick-quote")}
                      className={`h-9 w-9 shrink-0 border transition-[background-color,border-color,color,transform] hover:-translate-y-0.5 ${location.pathname === "/quick-quote" ? "border-amber-600 bg-amber-600 text-white hover:bg-amber-700 hover:text-white" : "border-amber-200 bg-amber-50 text-amber-700 hover:border-amber-300 hover:bg-amber-100 hover:text-amber-800"}`}
                      aria-label="Start AI Quick Quote" data-testid="topbar-quick-quote-button">
                      <WandSparkles className="h-[18px] w-[18px]" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" data-testid="topbar-quick-quote-tooltip">AI Quick Quote · Owner</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>

          <div className="mx-4 hidden max-w-md flex-1 items-center gap-4 md:flex">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                type="text"
                placeholder="Search leads, tasks..."
                className="pl-10"
                data-testid="global-search"
              />
            </div>
          </div>

          <div className="flex shrink-0 items-center gap-2 sm:gap-4">
            <NotificationsDropdown />
            
            {/* User Avatar in header */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 hover:bg-slate-100 rounded-lg p-1 transition-colors">
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={user?.picture} alt={user?.name} />
                    <AvatarFallback className="bg-indigo-100 text-indigo-700 text-sm">
                      {getInitials(user?.name)}
                    </AvatarFallback>
                  </Avatar>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-2 py-1.5">
                  <p className="text-sm font-medium">{user?.name}</p>
                  <p className="text-xs text-slate-500">{user?.email}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/settings")}>
                  <Settings className="w-4 h-4 mr-2" />
                  Settings
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                  <LogOut className="w-4 h-4 mr-2" />
                  Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page content - scrollable */}
        <main className={isFullBleedWorkspace ? "flex min-h-0 flex-1 overflow-hidden p-0" : "flex-1 overflow-y-auto p-4 md:p-6"} data-testid="crm-main-workspace">
          <ErrorBoundaryWrapper>
            <Outlet />
          </ErrorBoundaryWrapper>
        </main>
        
        {/* Walkthrough FAB - visible for managers */}
        {location.pathname !== "/dashboard" && location.pathname !== "/chat" && location.pathname !== "/cnc-router-station" && !location.pathname.startsWith("/production-sheets/") && (user?.role === "owner" || user?.role === "project_manager" || user?.role === "office_admin") && (
          <WalkthroughButton />
        )}
      </div>
    </div>
  );
};


function InstallAppButton() {
  const [deferredPrompt, setDeferredPrompt] = React.useState(null);
  const [showIOSGuide, setShowIOSGuide] = React.useState(false);
  const [isStandalone, setIsStandalone] = React.useState(false);

  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

  React.useEffect(() => {
    const standalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
    setIsStandalone(standalone);

    const handler = (e) => { e.preventDefault(); setDeferredPrompt(e); };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isStandalone) return null;

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      await deferredPrompt.userChoice;
      setDeferredPrompt(null);
    } else if (isIOS) {
      setShowIOSGuide(!showIOSGuide);
    }
  };

  return (
    <div className="px-4 pb-2">
      <button
        onClick={handleInstall}
        className="w-full flex items-center gap-3 p-2.5 rounded-lg bg-indigo-50 hover:bg-indigo-100 transition-colors text-left"
        data-testid="install-app-sidebar"
      >
        <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center shrink-0">
          {isIOS ? <Smartphone className="w-4 h-4 text-indigo-600" /> : <Download className="w-4 h-4 text-indigo-600" />}
        </div>
        <div className="min-w-0">
          <p className="text-sm font-medium text-indigo-700">Install App</p>
          <p className="text-[10px] text-indigo-500">{isIOS ? "Add to Home Screen" : "Quick access from phone"}</p>
        </div>
      </button>
      {showIOSGuide && isIOS && (
        <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800 space-y-1">
          <p className="font-semibold">How to install on iPhone/iPad:</p>
          <ol className="list-decimal pl-4 space-y-1">
            <li>Tap the <strong>Share</strong> button (square with arrow) at the bottom of Safari</li>
            <li>Scroll down and tap <strong>"Add to Home Screen"</strong></li>
            <li>Tap <strong>"Add"</strong> in the top right</li>
          </ol>
          <p className="text-amber-600 mt-1">Note: Must use Safari — Chrome/Firefox on iOS don't support this.</p>
        </div>
      )}
    </div>
  );
}


class ErrorBoundaryWrapper extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false, error: null }; }
  static getDerivedStateFromError(error) { return { hasError: true, error }; }
  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center h-64 text-center">
          <p className="text-red-600 font-medium mb-2">This page encountered an error</p>
          <p className="text-sm text-slate-500 mb-4">{this.state.error?.message}</p>
          <button onClick={() => { this.setState({ hasError: false }); window.location.href = "/dashboard"; }}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm hover:bg-indigo-700">
            Go to Dashboard
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
